
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface Shop {
  id: string;
  name: string;
  slug: string;
  email: string;
  phone?: string;
  shop_id: string;
  commission_rate?: number;
  created_at?: string;
  updated_at?: string;
  password_hash?: string;
}

interface ShopAuthContextType {
  shop: Shop | null;
  login: (shopId: string, password: string) => Promise<{ error: string | null }>;
  logout: () => void;
  loading: boolean;
}

const ShopAuthContext = createContext<ShopAuthContextType | undefined>(undefined);

export const useShopAuth = () => {
  const context = useContext(ShopAuthContext);
  if (context === undefined) {
    throw new Error('useShopAuth must be used within a ShopAuthProvider');
  }
  return context;
};

export const ShopAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [shop, setShop] = useState<Shop | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if shop is logged in on page load
    const shopData = localStorage.getItem('shop_auth');
    if (shopData) {
      try {
        const parsedShop = JSON.parse(shopData);
        setShop(parsedShop);
      } catch (error) {
        localStorage.removeItem('shop_auth');
      }
    }
    setLoading(false);
  }, []);

  const login = async (shopId: string, password: string) => {
    try {
      console.log('Attempting to login with shop ID:', shopId);

      // First, try to find the shop by shop_id
      const { data: shops, error: queryError } = await supabase
        .from('shops')
        .select('id, name, slug, email, phone, shop_id, commission_rate, created_at, updated_at, password_hash')
        .eq('shop_id', shopId);

      if (queryError) {
        console.error('Database query error:', queryError);
        return { error: 'Database connection failed' };
      }

      if (!shops || shops.length === 0) {
        console.log('No shop found with ID:', shopId);
        return { error: 'Invalid shop ID or password' };
      }

      const shopData = shops[0];
      console.log('Found shop:', shopData.name);

      // Hash the input password using the same function used during registration
      const { data: hashedInputPassword, error: hashError } = await supabase.rpc('hash_password', { 
        password: password 
      });

      if (hashError) {
        console.error('Error hashing password:', hashError);
        return { error: 'Authentication failed' };
      }

      // Compare the hashed input password with the stored hash
      if (shopData.password_hash !== hashedInputPassword) {
        console.log('Password mismatch');
        return { error: 'Invalid shop ID or password' };
      }

      const cleanShopData: Shop = {
        id: shopData.id,
        name: shopData.name,
        slug: shopData.slug,
        email: shopData.email,
        phone: shopData.phone || undefined,
        shop_id: shopData.shop_id,
        commission_rate: shopData.commission_rate || undefined,
        created_at: shopData.created_at || undefined,
        updated_at: shopData.updated_at || undefined,
        password_hash: shopData.password_hash || undefined
      };
      
      console.log('Login successful for shop:', cleanShopData.name);
      setShop(cleanShopData);
      localStorage.setItem('shop_auth', JSON.stringify(cleanShopData));

      return { error: null };
    } catch (error) {
      console.error('Login error:', error);
      return { error: 'Login failed due to an unexpected error' };
    }
  };

  const logout = () => {
    setShop(null);
    localStorage.removeItem('shop_auth');
  };

  return (
    <ShopAuthContext.Provider value={{ shop, login, logout, loading }}>
      {children}
    </ShopAuthContext.Provider>
  );
};
